import { motion } from 'framer-motion';
import { Building2, FileCheck, Landmark, LineChart, MapPinned, Users } from 'lucide-react';

const services = [
  { icon: MapPinned, title: 'Plotted development strategy', text: 'Micro-market selection, land-story positioning, inventory phasing, and launch readiness.' },
  { icon: LineChart, title: 'Sales absorption engine', text: 'Mandate-led demand creation designed to move premium inventory without eroding value.' },
  { icon: Landmark, title: 'Developer land banking', text: 'Structured access to plotted and residential opportunities across high-growth corridors.' },
  { icon: Users, title: 'Investor syndication', text: 'Private investor outreach, qualification, site-visit orchestration, and closure support.' },
  { icon: Building2, title: 'Residential mandates', text: 'Premium residential project positioning with developer-grade market intelligence.' },
  { icon: FileCheck, title: 'Documentation confidence', text: 'Clear-title coordination, transaction support, and transparent handover assistance.' },
];

const Services = () => {
  return (
    <section id="services" className="relative overflow-hidden bg-[#0B1F3B] py-24 text-[#F5F5F5] sm:py-32" data-testid="services-section">
      <div className="blueprint-grid-dark absolute inset-0 opacity-60" />
      <div className="absolute right-0 top-0 h-[32rem] w-[32rem] rounded-full bg-[#C5A880]/10 blur-3xl" />
      <div className="relative mx-auto max-w-7xl px-4 sm:px-6 lg:px-8">
        <div className="mb-14 grid gap-8 lg:grid-cols-[0.85fr_1.15fr] lg:items-end">
          <motion.div initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7 }}>
            <div className="mb-5 font-tech text-xs uppercase tracking-[0.28em] text-[#C5A880]">Sales engine</div>
            <h2 className="font-display text-3xl font-semibold leading-tight tracking-tight sm:text-4xl">A mandate desk built for velocity and precision.</h2>
          </motion.div>
          <motion.p initial={{ opacity: 0, y: 26 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ duration: 0.7, delay: 0.1 }} className="max-w-2xl text-base leading-relaxed text-white/65 lg:justify-self-end">
            Every service is designed around one outcome: credible absorption for premium Indian real estate.
          </motion.p>
        </div>

        <div className="grid gap-px overflow-hidden rounded-[2rem] border border-white/10 bg-white/10 md:grid-cols-2 lg:grid-cols-3" data-testid="services-grid">
          {services.map((service, index) => {
            const Icon = service.icon;
            return (
              <motion.article key={service.title} initial={{ opacity: 0, y: 24 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true, amount: 0.25 }} transition={{ duration: 0.55, delay: index * 0.06 }} className="group min-h-64 bg-[#0B1F3B] p-7 transition-colors duration-500 hover:bg-[#06101E]" data-testid={`service-card-${index + 1}`}>
                <div className="mb-12 flex items-start justify-between">
                  <div className="flex h-12 w-12 items-center justify-center rounded-full border border-[#C5A880]/30 bg-[#C5A880]/10 text-[#C5A880] transition-transform duration-500 group-hover:-rotate-6 group-hover:scale-110">
                    <Icon className="h-5 w-5" />
                  </div>
                  <span className="font-tech text-xs text-white/30">0{index + 1}</span>
                </div>
                <h3 className="font-display text-2xl font-semibold tracking-tight">{service.title}</h3>
                <p className="mt-4 text-sm leading-relaxed text-white/60">{service.text}</p>
              </motion.article>
            );
          })}
        </div>
      </div>
    </section>
  );
};

export default Services;
